import { UserSettingsClient, UserSettingsDto } from '../../../core/api-client';
import { ComponentStore } from '@ngrx/component-store';
import { computed, Injectable } from '@angular/core';
import { unwrap } from '../../../core/extensions';
import { switchMap, tap } from 'rxjs/operators';
import { UntilDestroy } from '@ngneat/until-destroy';
import { LocalStorageService } from '../../../core/services/local-storage.service';
import { state } from '@angular/animations';

interface UserSettingsState extends UserSettingsDto {
  loading: boolean
}

const MINUTES_BEFORE_TIMEOUT_KEY = 'minutes_before_timeout';

@Injectable({ providedIn: 'root' })
@UntilDestroy()
export class UserSettingsStore extends ComponentStore<UserSettingsState> {
  readonly minutesBeforeTimeOut$ = this.select(
    (state) => state.minutesBeforeTimeOut,
  );

  readonly loading = this.selectSignal((state) => state.loading);
  readonly chatRingtone = this.selectSignal((state) => state.chatRingtone);
  readonly chatRingtonePath = computed(() => `/assets/sound/${this.chatRingtone()?.fileName ?? 'Ringtone-default.wav'}`);

  constructor(
    private readonly userSettingsClient: UserSettingsClient,
    private readonly localStorageService: LocalStorageService,
  ) {
    super({
      hasNotificationOptout: false,
      hasEnabledNotificationSound: false,
      minutesBeforeTimeOut: parseInt(
        localStorageService.get(MINUTES_BEFORE_TIMEOUT_KEY) ?? '15',
        10,
      ),
      unreadMessagesIntervalMinutes: 10,
      chatRingtoneId: undefined,
      chatRingtone: undefined,
      loading: false
    });
  }

  refresh = this.effect(($trigger) =>
    $trigger.pipe(
      tap(() => this.update({ loading: true })),
      switchMap(() =>
        this.userSettingsClient.getUserSettings().pipe(
          unwrap<UserSettingsDto>(),
          tap((response) => this.update({...response, loading: false })),
        ),
      ),
    ),
  );

  update = this.updater((state, userSettings: Partial<UserSettingsState>) => {
    if (userSettings.minutesBeforeTimeOut) {
      this.localStorageService.set(
        MINUTES_BEFORE_TIMEOUT_KEY,
        userSettings.minutesBeforeTimeOut.toString(),
      );
    }
    return {
      ...state,
      ...userSettings,
    };
  });

  reset = this.updater(() => ({
    hasNotificationOptout: false,
    hasEnabledNotificationSound: false,
    minutesBeforeTimeOut: 15,
    unreadMessagesIntervalMinutes: 10,
    chatRingtoneId: undefined,
    chatRingtone: undefined,
    loading: false
  }));
}
